// ============================================
// PDP NOV - PRODUCT PAGE FUNCTIONALITY
// ============================================
// Handles variant selection, subscription toggle, and pricing updates

// ============================================
// PDP SUBSCRIPTION HELPERS
// ============================================
// Reusable helper functions for subscription and pricing logic

const giftNudge = document.querySelectorAll(".freeGiftNudgeTag");
const giftTitle = document.querySelectorAll(".pGiftProgressTitle");
const giftProgress = document.querySelectorAll(".pGiftProgress");

/**
 * Configuration for subscription functionality
 */
const FOUR_OZ_SKUS = ["TTFC-4OZ", "UFC-4OZ", "CAR-4OZ", "UFC-EOF-4OZ"];
// 2oz subscription is enabled only for these 3 products (per Aug 2026 task)
const TWO_OZ_SUBSCRIPTION_SKUS = ["UFC-2OZ", "TTFC-2OZ", "UFC-EOF-2OZ"];
const ROUTINE_BUNDLE_HANDLES = ["hs-warriors-daily-flare-care-bundle"];
const SUBSCRIPTION_CONFIG = {
  discountPercent: 15, // fallback when the selected plan's name has no "(X% OFF)" text
  deliveryFrequencies: {
    30: "Every month",
    60: "Every 2 months",
    120: "Every 4 months",
  },
  defaultFrequency: "60",
  // Unused — real plan ID comes straight from the select, not this map
  sellingPlans: {
    30: "26460225754",
    60: "26460291290",
    120: "26460225754",
  },
};

const PDP_CONFIG = {
  // Enable debug logging
  debug: false,
};

// Debug logger
function log(...args) {
  if (PDP_CONFIG.debug) {
    console.log("[PDPNOV]", ...args);
  }
}

/**
 * Format money to currency string
 * @param {number} amount - Amount in dollars (not cents)
 * @param {string} currencyCode - Currency code (default: USD)
 * @returns {string} Formatted currency string
 */
function formatMoney(amount, currencyCode = "USD") {
  const formatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount);
}

function updateProductGiftState(fours) {
  // console.log("Updating state");
  if (!giftTitle || !giftProgress) return;

  if (fours > 1) {
    giftTitle.forEach((title) => {
      title.innerHTML = "FREE GIFT is already in your cart!";
    });
    giftNudge.forEach((gift) => {
      gift.style.display = "none";
    });
    giftProgress.forEach((progress) => {
      progress.style.transform = "scaleX(1)";
      progress.style.background = "#47ae00";
    });
  } else if (fours == 1) {
    giftTitle.forEach((title) => {
      title.innerHTML = "1/2 products already in your cart!";
    });
    giftNudge.forEach((gift) => {
      gift.style.display = "block";
    });
    giftProgress.forEach((progress) => {
      progress.style.transform = "scaleX(0.5)";
      progress.style.background = "rgba(145, 145, 145, 1)";
    });
  } else {
    giftTitle.forEach((title) => {
      title.innerHTML = "0/2 products in your cart";
    });

    giftNudge.forEach((gift) => {
      gift.style.display = "none";
    });
    giftProgress.forEach((progress) => {
      progress.style.transform = "scaleX(0)";
      progress.style.background = "rgba(145, 145, 145, 1)";
    });
  }
}

function countFourOunceItems(cart) {
  var count = 0;
  cart.items.forEach(function (i) {
    if (FOUR_OZ_SKUS.length && i.sku && FOUR_OZ_SKUS.indexOf(i.sku) > -1) {
      count += i.quantity;
      return;
    }
    var vt = String(i.variant_title || "").toLowerCase();

    if (/\b4\s*oz\b|4[-\s]?ounce/.test(vt)) count += i.quantity;
    if (i.product_title.includes("Flare Care Duo")) count += i.quantity * 2;
  });
  return count;
}
/**
 * Get variant data from input element
 * @param {HTMLElement} input - Variant input element
 * @returns {Object} Variant data object
 */
function getVariantData(input) {
  if (!input) {
    console.warn("[PDP Helpers] getVariantData called with null input");
    return null;
  }

  const pricePlain = input.getAttribute("data-price-plain");
  const dpPlain = input.getAttribute("data-dp");
  const compareAtPlain = input.getAttribute("data-compare-at-price");
  const globalDiscountPercent = parseFloat(
    input.getAttribute("data-global-discount-percent") || "0"
  );

  // Parse price - Shopify returns price without currency symbol
  const price = pricePlain ? parseFloat(pricePlain.replace(/[^0-9.]/g, "")) : 0;
  const discountedPrice = dpPlain
    ? parseFloat(dpPlain.replace(/[^0-9.]/g, ""))
    : null;
  const compareAtPrice = compareAtPlain
    ? parseFloat(compareAtPlain.replace(/[^0-9.]/g, ""))
    : null;

  return {
    id: input.getAttribute("data-id"),
    price: price,
    discountedPrice: discountedPrice,
    compareAtPrice: compareAtPrice,
    globalDiscountPercent: Number.isNaN(globalDiscountPercent)
      ? 0
      : globalDiscountPercent,
    currencyCode: input.getAttribute("data-currency-code") || "USD",
    title: input.getAttribute("data-title"),
    sku: input.getAttribute("data-sku"),
    productHandle: input.getAttribute("data-product-handle"),
  };
}

/**
 * Calculate price based on purchase type and quantity
 * @param {number} basePrice - Base price per unit
 * @param {number} quantity - Quantity
 * @param {string} purchaseType - "subscription" or "onetime"
 * @returns {number} Final price
 */
function calculatePrice(basePrice, quantity, purchaseType) {
  let finalPrice = basePrice;

  if (purchaseType === "subscription") {
    // Apply subscription discount
    finalPrice = basePrice * (1 - getCurrentPlanDiscountPercent() / 100);
  }

  return finalPrice * quantity;
}

/**
 * Get the discount percent of the currently selected delivery frequency plan
 * @returns {number} Discount percent (e.g. 15, 10, 5)
 */
function getCurrentPlanDiscountPercent() {
  const select = document.querySelector("#delivery-frequency");
  const option = select ? select.options[select.selectedIndex] : null;
  if (option) {
    // Read % straight from the plan name's "(X% OFF)" text
    const match = option.textContent.match(/(\d+(?:\.\d+)?)\s*%/);
    if (match) return parseFloat(match[1]);
  }
  return SUBSCRIPTION_CONFIG.discountPercent;
}

/**
 * Get subscription price for a given base price
 * @param {number} basePrice - Base price
 * @returns {number} Subscription price with discount applied
 */
function getSubscriptionPrice(basePrice) {
  return basePrice * (1 - getCurrentPlanDiscountPercent() / 100);
}

function renderSelectorPrice(currentPrice, originalPrice, currencyCode) {
  const formattedCurrentPrice = formatMoney(currentPrice, currencyCode);

  if (!originalPrice || originalPrice <= currentPrice) {
    return formattedCurrentPrice;
  }

  return `
    <span class="sSubPriceOriginal">${formatMoney(originalPrice, currencyCode)}</span>
    <span class="sSubPriceCurrent">${formattedCurrentPrice}</span>
  `;
}

/**
 * Update Recharge hidden inputs in the form
 * @param {string} purchaseType - "subscription", "onetime", or "tbyb"
 * @param {string} frequency - Delivery frequency (30, 60, 120)
 * @param {string} variantId - Selected variant ID
 */
function updateRechargeInputs(purchaseType, frequency, variantId) {
  const intervalUnitInput = document.querySelector(".recharge-interval-unit");
  const subscriptionIdInput = document.querySelector(
    ".recharge-subscription-id"
  );
  const chargeFrequencyInput = document.querySelector(
    ".recharge-charge-frequency"
  );
  const sellingPlanSelect = document.querySelector(
    'select[name="selling_plan"]'
  );

  if (purchaseType === "subscription") {
    // Set Recharge properties for subscription
    if (intervalUnitInput) intervalUnitInput.value = "day";
    if (chargeFrequencyInput) chargeFrequencyInput.value = frequency;
    if (subscriptionIdInput) subscriptionIdInput.value = variantId;
    if (sellingPlanSelect) {
      const planId = SUBSCRIPTION_CONFIG.sellingPlans[frequency];
      if (planId) {
        sellingPlanSelect.value = planId;
      }
    }

    log("[PDP Helpers] Recharge inputs updated for subscription:", {
      frequency,
      variantId,
    });
  } else if (purchaseType === "tbyb") {
    // Set TBYB selling plan
    if (intervalUnitInput) intervalUnitInput.value = "";
    if (chargeFrequencyInput) chargeFrequencyInput.value = "";
    if (subscriptionIdInput) subscriptionIdInput.value = "";
    if (sellingPlanSelect && pdpState.tbybPlanId) {
      sellingPlanSelect.value = pdpState.tbybPlanId;
      log("[PDP Helpers] TBYB selling plan set to:", pdpState.tbybPlanId);
    } else {
      log("[PDP Helpers] WARNING: TBYB plan ID not found!");
    }
  } else {
    // Clear Recharge properties for one-time purchase
    if (intervalUnitInput) intervalUnitInput.value = "";
    if (chargeFrequencyInput) chargeFrequencyInput.value = "";
    if (subscriptionIdInput) subscriptionIdInput.value = "";
    if (sellingPlanSelect) sellingPlanSelect.value = "";

    log("[PDP Helpers] Recharge inputs cleared for one-time purchase");
  }
}

/**
 * Update the variant select dropdown (critical for add to cart)
 * @param {string} variantId - Variant ID to select
 */
function updateVariantSelect(variantId) {
  const variantSelect = document.querySelector('select[name="id"]');

  if (variantSelect && variantId) {
    variantSelect.value = variantId;
    log(`[PDP Helpers] Variant select updated to ID: ${variantId}`);

    // Trigger change event for any listeners
    const event = new Event("change", { bubbles: true });
    variantSelect.dispatchEvent(event);
  } else if (!variantSelect) {
    console.warn('[PDP Helpers] Variant select element (name="id") not found');
  }
}

/**
 * Update selling plan select if using Recharge native subscriptions
 * @param {string} frequency - Delivery frequency (30, 60, 120)
 * @param {boolean} isSubscription - Whether this is a subscription purchase
 */
function updateSellingPlanSelect(frequency, isSubscription) {
  const sellingPlanSelect = document.querySelector(
    'select[name="selling_plan"]'
  );

  if (!sellingPlanSelect) {
    log(
      "[PDP Helpers] No selling plan select found (using Recharge properties)"
    );
    return;
  }

  if (isSubscription && frequency) {
    const planId = SUBSCRIPTION_CONFIG.sellingPlans[frequency];
    if (planId && sellingPlanSelect) {
      sellingPlanSelect.value = planId;
      log(
        `[PDP Helpers] Selling plan updated to: ${planId} (${frequency} days)`
      );
    }
  } else {
    sellingPlanSelect.value = "";
    log("[PDP Helpers] Selling plan cleared for one-time purchase");
  }
}

/**
 * Update price displays on the page
 * @param {Object} params - Price update parameters
 * @param {Object} params.variantData - Current variant data
 * @param {number} params.quantity - Current quantity
 * @param {string} params.purchaseType - "subscription" or "onetime"
 */
function updatePriceDisplays({ variantData, quantity, purchaseType }) {
  if (!variantData) {
    console.warn(
      "[PDP Helpers] updatePriceDisplays called without variant data"
    );
    return;
  }

  const onetimePrice = variantData.discountedPrice || variantData.price;
  const subscriptionPrice = getSubscriptionPrice(variantData.price);

  // Update subscription selector prices
  const subscriptionPriceEl = document.querySelector(
    "[data-subscription-price]"
  );
  const onetimePriceEl = document.querySelector("[data-onetime-price]");

  if (subscriptionPriceEl) {
    subscriptionPriceEl.innerHTML = renderSelectorPrice(
      subscriptionPrice,
      variantData.price,
      variantData.currencyCode
    );
  }

  if (onetimePriceEl) {
    onetimePriceEl.innerHTML = renderSelectorPrice(
      onetimePrice,
      variantData.discountedPrice ? variantData.price : null,
      variantData.currencyCode
    );
  }

  // Update main price display
  const formPrice = document.querySelector(".grid-product__price");
  const finalPrice =
    purchaseType === "subscription"
      ? subscriptionPrice * quantity
      : onetimePrice * quantity;

  if (formPrice) {
    if (purchaseType === "subscription") {
      // Show subscription price with strikethrough of original
      const originalTotal = variantData.price * quantity;
      formPrice.innerHTML = `
        <span class="pdp-intro__form-price-was">${formatMoney(originalTotal, variantData.currencyCode)}</span>
        <span class="pdp-intro__form-price-now">${formatMoney(finalPrice, variantData.currencyCode)}</span>
      `;
    } else if (variantData.discountedPrice) {
      // Show discounted price for one-time (global discount)
      const originalTotal = variantData.price * quantity;
      formPrice.innerHTML = `
        <span class="pdp-intro__form-price-was">${formatMoney(originalTotal, variantData.currencyCode)}</span>
        <span class="pdp-intro__form-price-now">${formatMoney(finalPrice, variantData.currencyCode)}</span>
      `;
    } else if (variantData.compareAtPrice && variantData.compareAtPrice > variantData.price) {
      // Show Shopify compare_at_price as crossed-out
      const compareTotal = variantData.compareAtPrice * quantity;
      formPrice.innerHTML = `
        <span class="pdp-intro__form-price-was">${formatMoney(compareTotal, variantData.currencyCode)}</span>
        <span class="pdp-intro__form-price-now">${formatMoney(finalPrice, variantData.currencyCode)}</span>
      `;
    } else {
      formPrice.innerHTML = `<span class="pdp-intro__form-price-now">${formatMoney(finalPrice, variantData.currencyCode)}</span>`;
    }
  }

  // Update FREE GIFT badge (only show for one-time purchases)
  const giftBadge = document.querySelector(".freeGiftNudgeTag");
  if (giftBadge) {
    giftBadge.style.display = purchaseType === "onetime" ? "block" : "none";
  }

  log("[PDP Helpers] Price displays updated:", {
    onetimePrice,
    subscriptionPrice,
    finalPrice,
    purchaseType,
    quantity,
    globalDiscountPercent: variantData.globalDiscountPercent,
  });
}

/**
 * Update product variant title display
 * @param {string} variantTitle - Variant title to display
 */
function updateVariantTitle(variantTitle) {
  const variantTitleEl = document.querySelector(".productVariant.onChange");
  if (variantTitleEl && variantTitle) {
    variantTitleEl.textContent = variantTitle;
  }
}

// ============================================
// SUBSCRIPTION VISIBILITY (4oz only)
// ============================================

/**
 * Check if a variant should show the subscribe widget: any 4oz variant, or
 * a 2oz variant of one of the 3 products with 2oz subscription enabled.
 * @param {string} title - Variant title
 * @param {string} [sku] - Variant SKU
 * @returns {boolean}
 */
function isSubscriptionEligibleVariant(title, sku, productHandle) {
  if (title && /4\s*oz/i.test(title)) return true;
  if (sku && TWO_OZ_SUBSCRIPTION_SKUS.indexOf(sku.toUpperCase()) > -1) return true;
  if (productHandle && ROUTINE_BUNDLE_HANDLES.indexOf(productHandle) > -1) return true;
  return false;
}

/**
 * Swap the gift bullet + commitment wording + shipping badge for 4oz / 2oz / bundle.
 * @param {string} title - Variant title
 * @param {string} [sku] - Variant SKU
 */
function updateSubscriptionBenefitsForVariant(title, sku) {
  const isFourOz = !!(title && /4\s*oz/i.test(title));
  const isCombo = !!(
    title &&
    (/5\s*oz/i.test(title) || (/4\s*oz/i.test(title) && /1\s*oz/i.test(title)))
  );
  const isTwoOz = !!(sku && TWO_OZ_SUBSCRIPTION_SKUS.indexOf(sku.toUpperCase()) > -1);
  const giftBenefit = document.querySelector(".sSubBenefit--gift");
  if (giftBenefit) giftBenefit.style.display = isFourOz && !isCombo ? "" : "none";
  const commitmentText = document.querySelector("[data-commitment-text]");
  if (commitmentText) {
    commitmentText.textContent = isTwoOz
      ? "No commitment"
      : "No minimum commitment";
  }
  const singleSubscription = document.querySelector(".singleSubscription");
  if (singleSubscription) {
    singleSubscription.classList.toggle("is-two-oz-sub", isTwoOz);
  }
}

/**
 * Show or hide the subscription selector based on variant size.
 * When hidden, forces purchase type to "onetime" and clears selling plan.
 * @param {boolean} show - Whether to show the subscription selector
 */
function toggleSubscriptionSelector(show) {
  const subscriptionCtn = document.querySelector(".pSubscriptionCtn");
  if (!subscriptionCtn) {
    // No selling plans on this product — force one-time pricing
    pdpState.currentPurchaseType = "onetime";
    return;
  }

  const purchaseTypeWrap = subscriptionCtn.closest(".pdp-intro__purchase-type");

  if (show) {
    subscriptionCtn.style.display = "";
    if (purchaseTypeWrap) purchaseTypeWrap.classList.remove("is-hidden");
    log("[PDP Nov] Subscription selector shown (4oz variant)");
  } else {
    subscriptionCtn.style.display = "none";
    if (purchaseTypeWrap) purchaseTypeWrap.classList.add("is-hidden");
    log("[PDP Nov] Subscription selector hidden (non-4oz variant)");

    // Force one-time purchase
    const onetimeInput = document.querySelector('#onetime');
    if (onetimeInput) {
      onetimeInput.checked = true;
    }
    pdpState.currentPurchaseType = "onetime";

    // Clear selling plan
    const sellingPlanSelect = document.querySelector('select[name="selling_plan"]');
    if (sellingPlanSelect) {
      sellingPlanSelect.value = "";
    }
  }
}

// ============================================
// STATE MANAGEMENT
// ============================================

const pdpState = {
  currentPurchaseType: "subscription", // Can be "onetime", "subscription", or "tbyb" - defaults to subscription to match checked radio
  currentVariantData: null,
  currentQuantity: 1,
  currentDeliveryFrequency: SUBSCRIPTION_CONFIG.defaultFrequency,
  tbybPlanId: null, // Will be set from window.TBYB_PLAN_ID
};

// ============================================
// DOM ELEMENT REFERENCES
// ============================================

const PDPDOM = {
  subscriptionInputs: null,
  variantInputs: null,
  formPrice: null,
  quantity: null,
  productForm: null,
  deliverySelect: null,
  variantSelect: null,
  variantTitle: null,
};

// ============================================
// INITIALIZATION
// ============================================

function initDOM() {
  PDPDOM.subscriptionInputs = document.querySelectorAll(".subscriptionInput");
  PDPDOM.variantInputs = Array.from(
    document.querySelectorAll(".novProductInput")
  );
  PDPDOM.formPrice = document.querySelector(".grid-product__price");
  PDPDOM.quantity = (PDPDOM.productForm || document).querySelector(
    ".js-qty__wrapper input",
  );
  PDPDOM.productForm = document.querySelector('form[action*="/cart/add"]');
  PDPDOM.deliverySelect = document.querySelector("#delivery-frequency");
  PDPDOM.sellingPlanSelect = document.querySelector(
    'select[name="selling_plan"]'
  );
  PDPDOM.variantSelect = document.querySelector('select[name="id"]');
  PDPDOM.variantTitle = document.querySelector(".productVariant.onChange");

  // Debug logging
  log("[PDP Nov] Initialized with:");
  log("  - Variant inputs:", PDPDOM.variantInputs.length);
  log("  - Subscription inputs:", PDPDOM.subscriptionInputs.length);
  log("  - Variant select found:", !!PDPDOM.variantSelect);
  log("  - Form found:", !!PDPDOM.productForm);

  // Listen for cart updates to refresh gift state
  document.addEventListener("cart:updated", (e) => {
    const cart = e.detail.cart;
    if (cart) {
      const foursCount = countFourOunceItems(cart);
      updateProductGiftState(foursCount);
    }
  });

  // Get initial count from Liquid-rendered data attribute (avoids API call)
  const giftContainer = document.querySelector(".pGiftCtn[data-initial-four-oz-count]");
  if (giftContainer) {
    const initialCount = parseInt(giftContainer.dataset.initialFourOzCount, 10) || 0;
    log("[PDP Nov] Initial 4oz count from Liquid:", initialCount);
    updateProductGiftState(initialCount);
  }
}

function initializeState() {
  // Store TBYB plan ID if available
  if (window.TBYB_PLAN_ID) {
    pdpState.tbybPlanId = window.TBYB_PLAN_ID;
    log("[PDP Nov] TBYB Plan ID loaded:", pdpState.tbybPlanId);
  }

  // Initialize purchase type from checked subscription input
  const checkedSubscriptionInput = document.querySelector(
    ".subscriptionInput:checked"
  );
  if (checkedSubscriptionInput) {
    pdpState.currentPurchaseType = checkedSubscriptionInput.value;
    log("[PDP Nov] Initial purchase type:", pdpState.currentPurchaseType);
  }

  // Initialize delivery frequency from select FIRST
  if (PDPDOM.deliverySelect) {
    pdpState.currentDeliveryFrequency = PDPDOM.deliverySelect.value;
    log(
      "[PDP Nov] Initial delivery frequency:",
      pdpState.currentDeliveryFrequency
    );
  }

  // Initialize with first checked variant
  const checkedVariant = document.querySelector(".novProductInput:checked");

  if (checkedVariant) {
    pdpState.currentVariantData = getVariantData(checkedVariant);
    log("[PDP Nov] Initial variant:", pdpState.currentVariantData);

    // Show/hide subscription selector based on initial variant size
    const showSubscription = isSubscriptionEligibleVariant(
      pdpState.currentVariantData.title,
      pdpState.currentVariantData.sku,
      pdpState.currentVariantData.productHandle
    );
    toggleSubscriptionSelector(showSubscription);
    updateSubscriptionBenefitsForVariant(
      pdpState.currentVariantData.title,
      pdpState.currentVariantData.sku
    );

    // Update variant select to match checked variant
    updateVariantSelect(pdpState.currentVariantData.id);

    // Update variant title display
    updateVariantTitle(pdpState.currentVariantData.title);

    // Initialize prices and selling plan
    updateAllDisplays();
  } else {
    console.warn("[PDP Nov] No checked variant found on page load");
  }
}

// ============================================
// UPDATE FUNCTIONS
// ============================================

function updateAllDisplays() {
  if (!pdpState.currentVariantData) {
    console.warn("[PDP Nov] Cannot update displays without variant data");
    return;
  }

  // Update price displays
  updatePriceDisplays({
    variantData: pdpState.currentVariantData,
    quantity: pdpState.currentQuantity,
    purchaseType: pdpState.currentPurchaseType,
  });

  // Update Recharge properties
  updateRechargeInputs(
    pdpState.currentPurchaseType,
    pdpState.currentDeliveryFrequency,
    pdpState.currentVariantData.id
  );

  // Update selling plan select (for Shopify native subscriptions)
  const sellingPlanSelect = document.querySelector(
    'select[name="selling_plan"]'
  );
  if (sellingPlanSelect) {
    if (
      pdpState.currentPurchaseType === "subscription" &&
      pdpState.currentDeliveryFrequency
    ) {
      // Set the plan ID directly (currentDeliveryFrequency contains the plan ID)
      sellingPlanSelect.value = pdpState.currentDeliveryFrequency;
      log(
        `[PDP Nov] Selling plan select set to: ${pdpState.currentDeliveryFrequency}`
      );
    } else if (pdpState.currentPurchaseType === "tbyb" && pdpState.tbybPlanId) {
      // Set TBYB selling plan
      sellingPlanSelect.value = pdpState.tbybPlanId;
      log(`[PDP Nov] Selling plan select set to TBYB: ${pdpState.tbybPlanId}`);
    } else {
      // Clear for one-time purchase
      sellingPlanSelect.value = "";
      log("[PDP Nov] Selling plan select cleared (one-time purchase)");
    }
  }

  log("[PDP Nov] All displays updated");
}

function handleVariantChange(input) {
  const newVariantData = getVariantData(input);

  if (!newVariantData) {
    console.error("[PDP Nov] Failed to get variant data from input");
    return;
  }

  const url = new URL(window.location.href);
url.searchParams.set("variant", newVariantData.id);
window.history.pushState({}, '', url.toString());

  log("[PDP Nov] Variant changed to:", newVariantData.title);

  // Update state
  pdpState.currentVariantData = newVariantData;

  // Show/hide subscription selector based on variant size
  const showSubscription = isSubscriptionEligibleVariant(
    newVariantData.title,
    newVariantData.sku,
    newVariantData.productHandle
  );
  toggleSubscriptionSelector(showSubscription);
  updateSubscriptionBenefitsForVariant(newVariantData.title, newVariantData.sku);

  // CRITICAL: Update the variant select (theme is disabled, we control it now)
  updateVariantSelect(newVariantData.id);

  // Update variant title display
  updateVariantTitle(newVariantData.title);

  // Update all displays
  updateAllDisplays();
}

function handlePurchaseTypeChange(purchaseType) {
  log("[PDP Nov] Purchase type changed to:", purchaseType);

  pdpState.currentPurchaseType = purchaseType;

  // Update all displays (updates Recharge properties and prices)
  updateAllDisplays();
}

function handleDeliveryFrequencyChange(frequency) {
  log("[PDP Nov] Delivery frequency changed to:", frequency);

  pdpState.currentDeliveryFrequency = frequency;

  // Update Recharge properties
  updateRechargeInputs(
    pdpState.currentPurchaseType,
    frequency,
    pdpState.currentVariantData?.id
  );

  // Update selling plan select directly with the plan ID
  // Note: The delivery frequency select uses plan IDs as values, not day counts
  const sellingPlanSelect = document.querySelector(
    'select[name="selling_plan"]'
  );
  if (sellingPlanSelect && pdpState.currentPurchaseType === "subscription") {
    // The frequency value from the delivery select is actually the plan ID
    sellingPlanSelect.value = frequency;
    log(`[PDP Nov] Selling plan select updated to: ${frequency}`);
  } else if (sellingPlanSelect) {
    sellingPlanSelect.value = "";
    log("[PDP Nov] Selling plan select cleared (one-time purchase)");
  }

  // Update price displays — discount percent depends on the selected plan
  updatePriceDisplays({
    variantData: pdpState.currentVariantData,
    quantity: pdpState.currentQuantity,
    purchaseType: pdpState.currentPurchaseType,
  });
}

function handleQuantityChange(newQuantity) {
  const quantity = parseInt(newQuantity, 10);

  if (isNaN(quantity) || quantity < 1) {
    console.warn("[PDP Nov] Invalid quantity:", newQuantity);
    return;
  }

  pdpState.currentQuantity = quantity;
  log("[PDP Nov] Quantity changed to:", quantity);

  // Update price displays
  updatePriceDisplays({
    variantData: pdpState.currentVariantData,
    quantity: pdpState.currentQuantity,
    purchaseType: pdpState.currentPurchaseType,
  });
}

// ============================================
// EVENT LISTENERS
// ============================================

function attachEventListeners() {
  // Subscription type change listeners
  PDPDOM.subscriptionInputs.forEach((input) => {
    input.addEventListener("change", (e) => {
      handlePurchaseTypeChange(e.target.value);
    });
  });

  // Variant change listeners
  PDPDOM.variantInputs.forEach((input, ii) => {
    input.addEventListener("change", () => {
      log("[PDP Nov] Variant input changed:", {
        value: input.value,
        variantId: input.getAttribute("data-id"),
        hasDataVariantInput: input.hasAttribute("data-variant-input"),
        dataIndex: input.getAttribute("data-index"),
      });
      handleVariantChange(input);
      const flick = theme.sections.instances[1].flickity;
      console.log(flick);
      

      if (ii == 0) {
        flick.goToSlide(0);
      } else if (ii == 1) {
        flick.goToSlide(flick.childNavEls.length - 3);
      } else if (ii == 2) {
        flick.goToSlide(flick.childNavEls.length - 2);
      } else {
        flick.goToSlide(flick.childNavEls.length - 1);
      }

      // Check if the theme updated the select
      setTimeout(() => {
        const select = document.querySelector('select[name="id"]');
        if (select) {
          log("[PDP Nov] After variant change, select value is:", select.value);
        }
      }, 100);
    });
  });

  // Delivery frequency change listener
  if (PDPDOM.deliverySelect) {
    PDPDOM.deliverySelect.addEventListener("change", (e) => {
      handleDeliveryFrequencyChange(e.target.value);
    });
  }

  // Quantity change listeners
  if (PDPDOM.quantity) {
    // MutationObserver for quantity changes (some themes use this)
    const observer = new MutationObserver(() => {
      handleQuantityChange(PDPDOM.quantity.value);
    });

    observer.observe(PDPDOM.quantity, {
      attributes: true,
      attributeFilter: ["value"],
    });

    // Input event listener
    PDPDOM.quantity.addEventListener("input", (e) => {
      handleQuantityChange(e.target.value);
    });
  }

  // Plus/minus button listeners (scoped to product form — avoid cart drawer qty buttons)
  const qtyRoot = PDPDOM.productForm || document;
  const plusButton = qtyRoot.querySelector(".js-qty__adjust--plus");
  const minusButton = qtyRoot.querySelector(".js-qty__adjust--minus");

  if (plusButton) {
    plusButton.addEventListener("click", () => {
      setTimeout(() => {
        handleQuantityChange(PDPDOM.quantity.value);
      }, 50);
    });
  }

  if (minusButton) {
    minusButton.addEventListener("click", () => {
      setTimeout(() => {
        handleQuantityChange(PDPDOM.quantity.value);
      }, 50);
    });
  }

  // Form submission listener
  if (PDPDOM.productForm) {
    PDPDOM.productForm.addEventListener("submit", (e) => {
      log("[PDP Nov] ===== FORM SUBMISSION STARTING =====");

      // CRITICAL: Re-read the actual checked radio button to ensure state is correct
      const checkedSubscriptionInput = document.querySelector(
        ".subscriptionInput:checked"
      );
      if (checkedSubscriptionInput) {
        pdpState.currentPurchaseType = checkedSubscriptionInput.value;
        log(
          "[PDP Nov] Synced purchase type from checked radio:",
          pdpState.currentPurchaseType
        );
      }

      log("[PDP Nov] Current state:", {
        purchaseType: pdpState.currentPurchaseType,
        variantId: pdpState.currentVariantData?.id,
        quantity: pdpState.currentQuantity,
        TBYB_PLAN_ID: window.TBYB_PLAN_ID,
      });

      // DEBUG: Check what variant ID and selling plan are in the selects
      const variantSelect = document.querySelector('select[name="id"]');
      const sellingPlanSelect = document.querySelector(
        'select[name="selling_plan"]'
      );
      const selectedVariantId = variantSelect
        ? variantSelect.value
        : "NOT FOUND";
      const selectedSellingPlan = sellingPlanSelect
        ? sellingPlanSelect.value
        : "NOT FOUND";

      log("[PDP Nov] Select elements BEFORE final update:", {
        variantSelect: selectedVariantId,
        sellingPlanSelect: selectedSellingPlan,
      });

      // Ensure variant select is correct
      if (pdpState.currentVariantData) {
        updateVariantSelect(pdpState.currentVariantData.id);
      }

      // Update Recharge properties one final time
      updateRechargeInputs(
        pdpState.currentPurchaseType,
        pdpState.currentDeliveryFrequency,
        pdpState.currentVariantData?.id
      );

      // Update selling plan one final time - CRITICAL FOR TBYB
      if (pdpState.currentPurchaseType === "tbyb" && pdpState.tbybPlanId) {
        if (sellingPlanSelect) {
          sellingPlanSelect.value = pdpState.tbybPlanId;
          log("[PDP Nov] TBYB: Forcing selling plan to:", pdpState.tbybPlanId);
        } else {
          log("[PDP Nov] ERROR: Selling plan select not found!");
        }
      } else if (pdpState.currentPurchaseType === "subscription") {
        updateSellingPlanSelect(pdpState.currentDeliveryFrequency, true);
      } else {
        // Clear for one-time purchase - CRITICAL
        if (sellingPlanSelect) {
          sellingPlanSelect.value = "";
          log("[PDP Nov] ONE-TIME: Clearing selling plan select");
        }
      }

      // Do not submit legacy Recharge line item properties on one-time purchase
      document
        .querySelectorAll(
          ".recharge-interval-unit, .recharge-subscription-id, .recharge-charge-frequency"
        )
        .forEach((input) => {
          input.disabled = pdpState.currentPurchaseType === "onetime";
        });

      // DEBUG: Check again after update
      const finalSellingPlan = sellingPlanSelect
        ? sellingPlanSelect.value
        : "NOT FOUND";
      log("[PDP Nov] Select elements AFTER final update:", {
        variantSelect: variantSelect ? variantSelect.value : "NOT FOUND",
        sellingPlanSelect: finalSellingPlan,
      });

      // DEBUG: Log all form data that will be submitted
      const formData = new FormData(PDPDOM.productForm);
      log("[PDP Nov] Form data being submitted:");
      for (let [key, value] of formData.entries()) {
        log(`  ${key}: ${value}`);
      }
      log("[PDP Nov] ===== FORM SUBMISSION ENDING =====");
    });
  }

  log("[PDP Nov] Event listeners attached");
}

// ============================================
// INITIALIZATION
// ============================================

document.addEventListener("DOMContentLoaded", () => {
  log("[PDP Nov] Initializing...");

  initDOM();
  initializeState();
  attachEventListeners();

  log("[PDP Nov] Initialization complete");
});
